/**
 * Gallery — unified photo+video gallery (handoff §11, Phase 4 rewrite).
 *
 * Figma:
 *  - 318:4727 (default state)
 *  - 318:4749 (skeleton)
 *  - 40000032:6420 (error overlay — used by GenerationFailedModal in Phase 5.1)
 *
 * Composition (no inline view logic > ~30 lines):
 *  - HomeStatusBar
 *  - FilterChipsBar      → sticky top filter row
 *  - GalleryGrid         → 3-col 3:4 cards grouped by date + IO lazy load
 *  - GallerySkeleton     → initial load
 *  - GalleryEmptyState   → 0 items
 *
 * State preserved across viewer round-trips (session):
 *  - galleryFilter ('all' | 'photo' | 'video')
 *  - galleryScroll (px from top)
 *
 * Polling: every 5s while gallery contains any item with status='PROCESSING'
 * or 'PENDING'. Polling stops automatically when no processing items remain.
 *
 * Backend contract: GET /api/gallery?type=&limit=&offset= → { items, total, ... }
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  galleryInfiniteQueryOptions,
  useGalleryInfiniteQuery,
} from '../core/queries/gallery';
import GalleryGrid from '../components/gallery/GalleryGrid';
import FilterChipsBar from '../components/gallery/FilterChipsBar';
import GallerySkeleton from '../components/gallery/GallerySkeleton';
import GalleryEmptyState from '../components/gallery/GalleryEmptyState';
import GalleryWelcomeState from '../components/gallery/GalleryWelcomeState';
import GalleryErrorState from '../components/gallery/GalleryErrorState';
import { useStore } from '../store';
import { useAuthStore } from '../store/authStore';
import { session } from '../platform';
import { useConsentStore } from '../store/consentStore';
import useConsentGuard from '../hooks/useConsentGuard';
import { queryClient } from '../core/queryClient';

const POLL_INTERVAL_MS = 5000;

function readPersistedFilter() {
  try {
    return session.getItem('galleryFilter') || 'all';
  } catch {
    return 'all';
  }
}

function isProcessingGeneration(gen) {
  return gen?.status === 'PENDING' || gen?.status === 'PROCESSING';
}

function normalizeServerIds(gen) {
  return Array.isArray(gen?.serverIds)
    ? gen.serverIds.filter(Boolean).map(String)
    : [];
}

function itemTime(value) {
  const time = new Date(value || 0).getTime();
  return Number.isFinite(time) ? time : 0;
}

export function buildActivePhotoItems(activeGenerations = [], filter = 'all', galleryItems = []) {
  if (filter === 'video') return [];

  const photoGalleryItems = (galleryItems || [])
    .filter((item) => (item?.type || 'photo') === 'photo' && item?.id)
    .map((item) => ({ ...item, id: String(item.id), time: itemTime(item.createdAt) }));
  const galleryItemIds = new Set(photoGalleryItems.map((item) => item.id));
  const assignedFallbackIds = new Set();

  return [...activeGenerations]
    .filter((gen) => (gen.type || 'photo') === 'photo')
    .sort((a, b) => itemTime(a.createdAt) - itemTime(b.createdAt))
    .flatMap((gen) => {
      const requestedCount = Math.max(1, Number(gen.count) || 1);
      const serverIds = normalizeServerIds(gen);
      let completedCount = 0;

      if (serverIds.length > 0) {
        completedCount = serverIds.filter((id) => galleryItemIds.has(id)).length;
      } else {
        const startedAt = itemTime(gen.createdAt);
        const completedFallbackItems = photoGalleryItems
          .filter((item) => !assignedFallbackIds.has(item.id) && item.time >= startedAt)
          .slice(0, requestedCount);

        completedFallbackItems.forEach((item) => assignedFallbackIds.add(item.id));
        completedCount = completedFallbackItems.length;
      }

      const pendingCount = Math.max(0, requestedCount - completedCount);
      const pendingServerIds = serverIds.filter((id) => !galleryItemIds.has(id));

      return Array.from({ length: pendingCount }, (_, index) => ({
        id: `active-photo-${gen.id}-${pendingServerIds[index] || index}`,
        type: 'photo',
        status: gen.status || 'PROCESSING',
        errorMessage: gen.errorMessage || null,
        thumbnailUrl: null,
        imageUrl: null,
        aspectRatio: '3:4',
        createdAt: gen.createdAt || new Date().toISOString(),
      }));
    });
}

export default function Gallery() {
  const navigate = useNavigate();
  const activeGenerations = useStore((s) => s.activeGenerations);
  const reconcileActiveGenerations = useStore((s) => s.reconcileActiveGenerations);
  const authLoading = useAuthStore((s) => s.isLoading);
  const hasInitialized = useAuthStore((s) => s.hasInitialized);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const { requireConsent } = useConsentGuard();
  const initialFilter = readPersistedFilter();

  const [filter, setFilter] = useState(initialFilter);
  const containerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const prevActivePhotoCountRef = useRef(0);
  const galleryQuery = useGalleryInfiniteQuery(filter, {
    enabled: hasInitialized && !authLoading && !consentRequired,
  });
  const {
    data: galleryData,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: galleryLoading,
    refetch,
  } = galleryQuery;
  const items = useMemo(
    () => (galleryData?.pages || []).flatMap((page) => page?.items || []),
    [galleryData],
  );
  const isWelcome = consentRequired === true;
  const loading = !isWelcome && (!hasInitialized || authLoading || galleryLoading);
  const hasMore = Boolean(hasNextPage);

  // Persist filter
  useEffect(() => {
    if (isWelcome) return;
    try {
      session.setItem('galleryFilter', filter);
    } catch {
      /* quota / private-mode */
    }
  }, [filter, isWelcome]);

  const activePhotoItems = useMemo(() => {
    return buildActivePhotoItems(activeGenerations, filter, items);
  }, [activeGenerations, filter, items]);

  const activePhotoCount = activePhotoItems.length;
  const activeProcessingPhotoCount = activePhotoItems
    .filter(isProcessingGeneration)
    .length;
  const visibleItems = useMemo(
    () => [...activePhotoItems, ...items],
    [activePhotoItems, items],
  );

  useEffect(() => {
    if (isWelcome) return;
    reconcileActiveGenerations(items);
  }, [isWelcome, items, reconcileActiveGenerations]);

  // Polling for processing items
  useEffect(() => {
    if (isWelcome) return undefined;
    const hasProcessing = items.some(
      (i) => i.status === 'PROCESSING' || i.status === 'PENDING',
    );
    if (!hasProcessing) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return undefined;
    }
    if (pollIntervalRef.current) return undefined;
    pollIntervalRef.current = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isWelcome, items, refetch]);

  useEffect(() => {
    if (isWelcome) return undefined;
    if (activeProcessingPhotoCount > 0) {
      prevActivePhotoCountRef.current = activeProcessingPhotoCount;
      const id = setInterval(() => {
        refetch();
      }, POLL_INTERVAL_MS);
      return () => clearInterval(id);
    }

    if (prevActivePhotoCountRef.current > 0) {
      prevActivePhotoCountRef.current = 0;
      refetch();
    }

    return undefined;
  }, [activeProcessingPhotoCount, isWelcome, refetch]);

  // Restore scroll position on first non-loading paint
  useEffect(() => {
    if (isWelcome) {
      window.scrollTo(0, 0);
      return;
    }
    if (loading) return;
    try {
      const saved = session.getItem('galleryScroll');
      if (saved && typeof window !== 'undefined') {
        window.scrollTo(0, parseInt(saved, 10) || 0);
      }
    } catch {
      /* quota */
    }
  }, [isWelcome, loading]);

  const persistScroll = useCallback(() => {
    try {
      const scrollTop =
        typeof window !== 'undefined'
          ? window.scrollY || document.documentElement.scrollTop || 0
          : 0;
      session.setItem(
        'galleryScroll',
        String(scrollTop),
      );
    } catch {
      /* quota */
    }
  }, []);

  const handleItemClick = useCallback(
    (item) => {
      persistScroll();
      // Photo viewer + video viewer both resolve through this URL pattern
      // (viewer router reads ?type= to decide which page to render).
      navigate(`/gallery/${item.id}?type=${item.type}`, { state: { from: '/gallery' } });
    },
    [navigate, persistScroll],
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const isEmpty =
    consentRequired === false
    && !loading
    && !error
    && visibleItems.length === 0;
  const handleEmptyStart = useCallback(() => {
    navigate(filter === 'video' ? '/photo-catalog' : '/prompt/general');
  }, [filter, navigate]);
  const openWelcomeCreation = useCallback(() => {
    navigate('/photo-catalog');
  }, [navigate]);
  const continueAfterConsent = useCallback(() => {
    openWelcomeCreation();
    void Promise.allSettled([
      Promise.resolve(refreshUser()),
      queryClient.prefetchInfiniteQuery(galleryInfiniteQueryOptions('all')),
    ]);
  }, [openWelcomeCreation, refreshUser]);
  const handleWelcomeStart = useCallback(() => {
    requireConsent(continueAfterConsent, 'generate');
  }, [continueAfterConsent, requireConsent]);

  if (isWelcome) {
    return (
      <div
        className="h-[100dvh] overflow-hidden bg-bg-app text-text-primary"
        data-testid="gallery-page"
      >
        <GalleryWelcomeState onStart={handleWelcomeStart} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-bg-app text-text-primary"
      data-testid="gallery-page"
    >
      <div
        ref={containerRef}
        className="pb-24"
        style={{
          paddingTop:
            'calc(var(--safe-top, 0px) + 112px - var(--page-content-top-shift, 0px))',
        }}
      >
        <div
          className="sticky z-20 bg-bg-app py-2 relative"
          style={{
            top:
              'calc(var(--safe-top, 0px) + 82px - var(--page-content-top-shift, 0px))',
          }}
          data-testid="gallery-filter-sticky"
        >
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-full h-40 bg-bg-app pointer-events-none"
          />
          <FilterChipsBar
            value={filter}
            onChange={setFilter}
            loading={loading}
          />
        </div>

        {((!hasInitialized || authLoading) || (loading && activePhotoCount === 0 && visibleItems.length === 0)) && (
          <GallerySkeleton />
        )}

        {!loading && error && (
          <GalleryErrorState
            onRetry={() => {
              refetch();
            }}
          />
        )}

        {isEmpty && (
          <GalleryEmptyState
            filter={filter}
            onStart={handleEmptyStart}
          />
        )}

        {!error && !isEmpty && (
          <GalleryGrid
            items={visibleItems}
            onItemClick={handleItemClick}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        )}
      </div>
    </div>
  );
}
